import { getCostAnalysisPayeeLabel, getCostAnalysisPayeeValue } from "./costAnalysisPayee";

const money = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const currencyMeta = (currencyValue) => {
  const code = String(currencyValue || "PHP").toUpperCase();
  return {
    code,
    symbol: code === "USD" ? "$" : "₱",
  };
};

const renderMoneyAmount = (value, currencyValue) => {
  const { symbol } = currencyMeta(currencyValue);
  return `${symbol}${money(value)}`;
};

// Render dual amount (USD and PHP) when currency is USD
const renderDualAmount = (baseAmount, currencyValue, exchangeRate) => {
  const { code } = currencyMeta(currencyValue);
  const rate = Number(exchangeRate || 1);

  if (code === "USD" && rate > 0) {
    const phpAmount = Number(baseAmount || 0) * rate;
    return `
      <div>$${money(baseAmount)}</div>
      <div style="font-size:11px;color:#555;">₱${money(phpAmount)}</div>
    `;
  }

  return `₱${money(baseAmount)}`;
};

// Render dual computed amount (show USD equivalent and PHP)
const renderDualComputedAmount = (computedPhpAmount, currencyValue, exchangeRate) => {
  const { code } = currencyMeta(currencyValue);
  const rate = Number(exchangeRate || 1);

  if (code === "USD" && rate > 0) {
    const usdAmount = Number(computedPhpAmount || 0) / rate;
    return `
      <div>$${money(usdAmount)}</div>
      <div style="font-size:11px;color:#555;">₱${money(computedPhpAmount)}</div>
    `;
  }

  return `₱${money(computedPhpAmount)}`;
};

export const buildCostAnalysisReportHtml = ({
  request,
  shipment,
  reportData,
  billingRows = [],
  receiptedRows = [],
  actualRows = [],
  receiptedSubtotal = 0,
  grandTotalValue = 0,
  actualCostTotalValue = 0,
  grossProfitValue = 0,
  calculateBillingAmount,
  connectedRequests = [],
  attachedDocuments = [],
}) => {
  const escapeHtml = (value) =>
    String(value || "").replace(/[&<>"']/g, (char) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    }[char]));

  const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "webp", "gif"];
  const isImageDocument = (doc) => {
    const source = String(doc?.file_type || doc?.url || "").toLowerCase();
    return IMAGE_EXTENSIONS.some((ext) => source.includes(ext));
  };
  const imageDocuments = attachedDocuments.filter((doc) => doc?.url && isImageDocument(doc));
  const linkedDocuments = attachedDocuments.filter((doc) => !(doc?.url && isImageDocument(doc)));
  const resolveBillingAmount = (row) => {
    if (row?.computed_amount !== undefined && row?.computed_amount !== null) {
      return row.computed_amount;
    }
    if (typeof calculateBillingAmount === "function") {
      return calculateBillingAmount(row);
    }
    return Number(row?.base_amount || 0);
  };

  const costAnalysisStage = String(
    reportData?.status ||
      request?.cost_analysis_stage ||
      request?.cost_analysis?.status ||
      ""
  ).toLowerCase();
  const user130SignatureUrl = ["partially_approved", "verified", "approved", "completed"].includes(costAnalysisStage)
    ? reportData?.verified_by_signature_url ||
      request?.cost_analysis?.verified_by_signature_url ||
      ""
    : "";
  // Keep assigned approver names visible in drafts, but only show signatures
  // after the corresponding approval stage has actually been completed.
  const user130Name =
    reportData?.initial_approver_name ||
    request?.cost_analysis?.initial_approver_name ||
    reportData?.verified_by_name ||
    request?.cost_analysis?.verified_by_name ||
    "—";
  const canShowFinalApproval = ["approved", "completed"].includes(costAnalysisStage);
  const user127SignatureUrl = canShowFinalApproval ? reportData?.approver_signature_url || "" : "";
  const user127Name = reportData?.final_approver_name || reportData?.approver_name || "—";
  const joPayeeLabel = request?.jo_payee_label || getCostAnalysisPayeeLabel(shipment);
  // Use saved/edited consignee from reportData first, fallback to rule of payee
  const joPayee =
    reportData?.consignee ||
    getCostAnalysisPayeeValue(shipment, request?.payee || request?.jo_payee) ||
    "—";
  const billTo =
    reportData?.bill_to ||
    (reportData?.bill_to_same_as_consignee ? joPayee : "") ||
    "—";

  const billingVatSummary = billingRows.reduce(
    (summary, row) => {
      const currency = String(row?.currency || "PHP").toUpperCase();
      const exchangeRate = currency === "USD" ? Number(row?.exchange_rate || 1) || 1 : 1;
      const baseAmountPhp = Number(row?.base_amount || 0) * exchangeRate;
      summary.vatExclusive += baseAmountPhp;
      if (row?.has_output_vat) {
        summary.vat += baseAmountPhp * 0.12;
        summary.hasVat = true;
      }
      return summary;
    },
    { vatExclusive: 0, vat: 0, hasVat: false }
  );
  const billingEwtSummary = billingRows.reduce(
    (summary, row) => {
      const rate = Number(row?.ewt_rate || 0);
      if (!row?.has_ewt || rate <= 0) return summary;

      const computedAmount = Number(resolveBillingAmount(row) || 0);
      const currency = String(row?.currency || "PHP").toUpperCase();
      const exchangeRate = currency === "USD" ? Number(row?.exchange_rate || 1) || 1 : 1;
      const baseAmountPhp = Number(row?.base_amount || 0) * exchangeRate;
      const vatAmount = row?.has_output_vat ? baseAmountPhp * 0.12 : 0;
      const amountBeforeEwt = row?.computed_amount !== undefined && row?.computed_amount !== null && row?.computed_amount !== ""
        ? computedAmount / (1 + rate / 100)
        : baseAmountPhp + vatAmount;
      const ewtAmount = Math.max(computedAmount - amountBeforeEwt, 0);
      const key = String(rate);

      summary.total += ewtAmount;
      summary.byRate[key] = (summary.byRate[key] || 0) + ewtAmount;
      return summary;
    },
    { total: 0, byRate: {} }
  );
  const hasBillingBreakdown = billingVatSummary.hasVat || billingEwtSummary.total > 0;
  const ewtSummaryRowsHtml = Object.entries(billingEwtSummary.byRate)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(
      ([rate, amount]) => `
      <tr class="vat-summary-row">
        <td colspan="6" class="right">EWT ${rate}%</td>
        <td class="right">${renderMoneyAmount(amount, "PHP")}</td>
      </tr>
    `
    )
    .join("");
  const vatSummaryRowsHtml = hasBillingBreakdown
    ? `
      ${billingVatSummary.hasVat ? `
        <tr class="vat-summary-row">
          <td colspan="6" class="right">Total Cost / VAT Exclusive</td>
          <td class="right">${renderMoneyAmount(billingVatSummary.vatExclusive, "PHP")}</td>
        </tr>
        <tr class="vat-summary-row">
          <td colspan="6" class="right">VAT 12%</td>
          <td class="right">${renderMoneyAmount(billingVatSummary.vat, "PHP")}</td>
        </tr>
      ` : ""}
      ${ewtSummaryRowsHtml}
      <tr class="vat-summary-row">
        <td colspan="6" class="right">Total Cost / VAT Inclusive (SUB TOTAL)</td>
        <td class="right">${renderMoneyAmount(
          billingVatSummary.vatExclusive + billingVatSummary.vat + billingEwtSummary.total,
          "PHP"
        )}</td>
      </tr>
    `
    : "";

  return `
    <html>
      <head>
        <title>Cost Analysis ${request?.rfp_no || ""}</title>
        <style>
          *{box-sizing:border-box;}
          body{font-family:"Times New Roman",serif;color:#111;margin:0;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact;}
          .page{max-width:780px;margin:0 auto;padding:10px 14px 14px;}
          h1{text-align:center;margin:0;font-size:18px;letter-spacing:.7px;color:#1f3b64;}
          h2{text-align:center;margin:2px 0 0;font-size:11px;font-weight:700;}
          .header-grid{margin-top:8px;display:grid;grid-template-columns:1fr 1fr;gap:3px 16px;font-size:9px;border:1px solid #111;padding:6px 8px;}
          .header-grid .label{font-weight:700;}
          .section-title{margin-top:7px;background:#1f3b64;color:#fff;padding:4px 8px;font-size:10px;font-weight:700;letter-spacing:.25px;}
          table{width:100%;border-collapse:collapse;margin-top:0;font-size:9px;}
          th,td{border:1px solid #111;padding:3px 4px;vertical-align:top;}
          th{background:#f3f5f8;text-align:left;}
          .right{text-align:right;}
          .subtotal-row{background:#e8f5e9;font-weight:700;}
          .grand-total-row{background:#1f3b64;color:#fff;font-weight:700;}
          .vat-summary-row{background:#e8f5e9;font-weight:700;}
          .gross-profit-row{background:#4caf50;color:#fff;font-weight:700;}
          .billing-table{table-layout:fixed;}
          .billing-table .computed-col{white-space:nowrap;}
          .billing-table .currency-col{white-space:nowrap;text-align:center;}
          .billing-table .rate-col{white-space:nowrap;text-align:right;}
          .signatures{margin-top:14px;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:6px;page-break-inside:avoid;break-inside:avoid;}
          .sig-box{text-align:center;padding:4px;min-width:0;}
          .sig-label{font-size:10px;color:#555;margin-bottom:4px;white-space:nowrap;}
          .sig-image{height:44px;display:flex;align-items:center;justify-content:center;margin-bottom:4px;}
          .sig-image img{max-height:44px;max-width:150px;object-fit:contain;}
          .sig-name{font-weight:700;font-size:13px;border-top:1px solid #111;padding-top:4px;min-height:20px;}
          .sig-title{font-size:11px;color:#555;margin-top:2px;}
          .attachment-page{page-break-before:always;break-before:page;padding-top:24px;}
          .attachment-page .attachment-title{font-size:14px;font-weight:700;margin-bottom:10px;color:#1f3b64;}
          .attachment-page img{max-width:100%;max-height:1400px;display:block;margin:0 auto;object-fit:contain;}
          .attachment-links td a{color:#1f3b64;text-decoration:underline;}
          @page{size:A4 portrait;margin:8mm;}
          @media print{
            html,body{width:100%;margin:0;background:#fff;}
            .page{width:100%;max-width:none;margin:0;padding:0;font-size:9px;}
            *,*::before,*::after{-webkit-print-color-adjust:exact;print-color-adjust:exact;}
          }
        </style>
      </head>
      <body>
        <div class="page">
          <h1>RCS LOGISTICS PHILS., INC.</h1>
          <h2>COST ANALYSIS</h2>

          <div class="header-grid">
            <div><span class="label">DATE:</span> ${reportData?.report_date_created || new Date().toLocaleDateString()}</div>
            <div><span class="label">J.O NO.:</span> ${request?.job_order || "—"}</div>
            <div><span class="label">${joPayeeLabel.toUpperCase()}:</span> ${joPayee}</div>
            <div><span class="label">DOCK:</span> ${reportData?.dock || "—"}</div>
            <div><span class="label">BILL TO:</span> ${billTo}</div>
            <div><span class="label">P.O. NO.:</span> ${reportData?.po_no || "—"}</div>
            <div><span class="label">BL/AWB:</span> ${reportData?.tracking_no || "—"}</div>
            <div><span class="label">SHIPMENT:</span> ${reportData?.shipment_details || "—"}</div>
            <div><span class="label">REFERENCE NO.:</span> ${reportData?.reference_no || "—"}</div>
          </div>

          <div class="section-title">BILLING CHARGES</div>
          <table class="billing-table">
            <thead>
              <tr>
                <th style="width:27%">Particulars</th>
                <th class="currency-col" style="width:11%">Currency</th>
                <th class="rate-col" style="width:10%">Rate</th>
                <th style="width:11%">Output VAT</th>
                <th style="width:9%">EWT</th>
                <th style="width:15%" class="right">Base Amount</th>
                <th style="width:16%" class="right computed-col">Computed</th>
              </tr>
            </thead>
            <tbody>
              ${billingRows
                .map(
                  (row) => `
                <tr>
                  <td>${row.particulars || "—"}</td>
                  <td class="currency-col">${currencyMeta(row.currency).code}</td>
                  <td class="right rate-col">${Number(row.exchange_rate || 1).toFixed(4)}</td>
                  <td>${row.has_output_vat ? "12%" : "—"}</td>
                  <td>${row.has_ewt && row.ewt_rate ? row.ewt_rate + "%" : "—"}</td>
                  <td class="right">${renderDualAmount(row.base_amount, row.currency, row.exchange_rate)}</td>
                  <td class="right computed-col">${renderDualComputedAmount(resolveBillingAmount(row), row.currency, row.exchange_rate)}</td>
                </tr>
              `
                )
                .join("")}
              ${vatSummaryRowsHtml}
            </tbody>
          </table>

          <div class="section-title">RECEIPTED CHARGES</div>
          <table>
            <thead>
              <tr>
                <th style="width:40%">Particulars</th>
                <th style="width:30%">O.R. NO.</th>
                <th style="width:30%" class="right">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${receiptedRows
                .map(
                  (row) => `
                <tr>
                  <td>${row.particulars || "—"}</td>
                  <td>${row.or_no || "—"}</td>
                  <td class="right">${money(row.amount)}</td>
                </tr>
              `
                )
                .join("")}
              <tr class="subtotal-row">
                <td colspan="2" class="right">SUBTOTAL</td>
                <td class="right">${renderMoneyAmount(receiptedSubtotal, "PHP")}</td>
              </tr>
            </tbody>
          </table>

          <table style="margin-top:12px;">
            <tr class="grand-total-row">
              <td style="width:70%" class="right">GRAND TOTAL</td>
              <td style="width:30%" class="right">${renderMoneyAmount(grandTotalValue, "PHP")}</td>
            </tr>
          </table>

          <div class="section-title">ACTUAL COST</div>
          <table>
            <thead>
              <tr>
                <th style="width:40%">Particulars</th>
                <th style="width:30%">O.R. NO.</th>
                <th style="width:30%" class="right">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${actualRows
                .map(
                  (row) => `
                <tr>
                  <td>${row.particulars || "—"}</td>
                  <td>${row.or_no || "—"}</td>
                  <td class="right">${money(row.amount)}</td>
                </tr>
              `
                )
                .join("")}
              <tr class="subtotal-row">
                <td colspan="2" class="right">TOTAL COST</td>
                <td class="right">${renderMoneyAmount(actualCostTotalValue, "PHP")}</td>
              </tr>
            </tbody>
          </table>

          <table style="margin-top:12px;">
            <tr class="gross-profit-row">
              <td style="width:70%" class="right">GROSS PROFIT</td>
              <td style="width:30%" class="right">${renderMoneyAmount(grossProfitValue, "PHP")}</td>
            </tr>
          </table>

          ${(() => {
            const requestsWithRemarks = connectedRequests.filter((r) => r.remarks && r.remarks.trim());
            if (!requestsWithRemarks.length) return "";
            return `
              <div class="section-title">REQUEST REMARKS</div>
              <table>
                <thead>
                  <tr>
                    <th style="width:20%">RFP No.</th>
                    <th style="width:20%" class="right">Amount</th>
                    <th style="width:60%">Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  ${requestsWithRemarks
                    .map(
                      (r) => `
                    <tr>
                      <td>${r.rfp_no || "—"}</td>
                      <td class="right">${renderMoneyAmount(
                        r.total_amount ?? r.amount ?? r.requested_amount ?? 0,
                        r.currency || "PHP"
                      )}</td>
                      <td>${r.remarks || "—"}</td>
                    </tr>
                  `
                    )
                    .join("")}
                </tbody>
              </table>
            `;
          })()}

          <div class="signatures">
            <div class="sig-box">
              <div class="sig-label">PREPARED BY:</div>
              <div class="sig-image">
                ${reportData?.preparer_signature_url ? `<img src="${reportData.preparer_signature_url}" alt="Signature" />` : ""}
              </div>
              <div class="sig-name">${reportData?.preparer_name || "—"}</div>
              <div class="sig-title">Declarant</div>
            </div>
            <div class="sig-box">
              <div class="sig-label">PARTIALLY APPROVED BY:</div>
              <div class="sig-image">
                ${user130SignatureUrl ? `<img src="${user130SignatureUrl}" alt="Signature" />` : ""}
              </div>
              <div class="sig-name">${user130Name}</div>
              <div class="sig-title">Initial Approver</div>
            </div>
            <div class="sig-box">
              <div class="sig-label">FINAL APPROVED BY:</div>
              <div class="sig-image">
                ${user127SignatureUrl ? `<img src="${user127SignatureUrl}" alt="Signature" />` : ""}
              </div>
              <div class="sig-name">${user127Name}</div>
              <div class="sig-title">Final Approver</div>
            </div>
          </div>

          ${linkedDocuments.length
            ? `
            <div class="section-title">ATTACHED DOCUMENTS</div>
            <table class="attachment-links">
              <thead>
                <tr>
                  <th style="width:20%">Category</th>
                  <th style="width:40%">Document</th>
                  <th style="width:40%">Link</th>
                </tr>
              </thead>
              <tbody>
                ${linkedDocuments
                  .map(
                    (doc) => `
                  <tr>
                    <td>${escapeHtml(doc.category === "track_n_trace" ? "Track N Trace" : "Accounting")}</td>
                    <td>${escapeHtml(doc.title)}${doc.subtitle ? `<br/><span style="font-size:11px;color:#555;">${escapeHtml(doc.subtitle)}</span>` : ""}</td>
                    <td>${doc.url ? `<a href="${escapeHtml(doc.url)}" target="_blank" rel="noopener noreferrer">Open document</a>` : "—"}</td>
                  </tr>
                `
                  )
                  .join("")}
              </tbody>
            </table>
          `
            : ""}
        </div>

        ${imageDocuments
          .map(
            (doc) => `
          <div class="page attachment-page">
            <div class="attachment-title">${escapeHtml(doc.category === "track_n_trace" ? "Track N Trace" : "Accounting")} — ${escapeHtml(doc.title)}</div>
            <img src="${escapeHtml(doc.url)}" alt="${escapeHtml(doc.title)}" />
          </div>
        `
          )
          .join("")}
      </body>
    </html>
  `;
};
